import { DatelyObject, Dates, Numeric, Years } from "../../util";
import { Translations } from "../../logic";
import { SubsidisedCommuteKind, SubsidisedCommuteUsecase, UserDefinedRow } from "../../model";
import { UsecaseValidationMessage } from "../UsecaseValidationMessage";
import { BaseUsecaseLogic } from "../BaseUsecaseLogic";

/** Client-side logic for SubsidisedCommuteUsecase  */
export class SubsidisedCommuteUsecaseLogic extends BaseUsecaseLogic {

  private _calculatedTaxable: number;
  private _calculatedTaxfree: number;
  private _isBefore2021: boolean;
  private _row: UserDefinedRow;
  private _date: DatelyObject;

  /** Returns calculated taxable value. */
  public get calculatedTaxable(): number {
    return this._calculatedTaxable;
  }

  /** Returns calculated taxfree value */
  public get calculatedTaxfree(): number {
    return this._calculatedTaxfree;
  }

  /** Returns true if the date for calculation is before 2021. */
  public get isBefore2021(): boolean {
    return this._isBefore2021 ?? false;
  }

  /**
   * Updates the usecase values based on user input without going to the server.
   * @param row Row to update.
   * @param date The reference date for which the logic is done.
   * Typically the salary date of a calculation and the typically, the information needed is just the year for which the yearly changing numbers are fetched, but may later have more extended features.
   */
  public updateUsecase(row: UserDefinedRow, date: DatelyObject): void {
    row = row ?? this._row;
    this._row = row;
    date = date ?? this._date;
    this._date = date;

    const data: SubsidisedCommuteUsecase = row.data ?? {};
    if (!data.kind || data.kind == SubsidisedCommuteKind.NoDeduction || data.kind == SubsidisedCommuteKind.Undefined)
    {
        data.kind = SubsidisedCommuteKind.NoDeduction;
        data.deduction = null;
    }
    else
    {
      data.deduction = data.deduction && data.deduction < 0 ? null : data.deduction;
    }

    data.taxable = data.taxable && data.taxable < 0 ? null : data.taxable;

    if (data.kind == SubsidisedCommuteKind.PeriodicalDeduction) {
      data.periodDivider =  data.periodDivider ?? 12;
      data.periodDivider = data.periodDivider < 0 ? 12 : data.periodDivider;
      if ( data.periodDivider  == 0) {
        data.kind = SubsidisedCommuteKind.SingleDeduction;
      }
    }
    else
    {
      data.periodDivider = null;
    }

    if (Dates.isSameOrBefore(date, "2021-01-01")) {
      data.taxable = null;
      this._isBefore2021 = true;
      return;
    }
    this._isBefore2021 = false;

    const limit = Years.getYearlyChangingNumbers(date)?.sideCosts?.taxFreeSubsidisedCommute ?? 0;
    let total = Numeric.round((row.price ?? 0) * (row.count ?? 1), 2) ?? 0;
    if (total < 0) {
      total = 0;
    }

    let deduction = data.deduction ?? 0;
    if (deduction > total) {
      deduction = 0;
      data.deduction = null;
    }
    if (data.taxable && data.taxable > ( total - deduction) ) {
      data.taxable = null;
    }

    const calcTaxable = Numeric.round(Math.max(0, total - deduction - limit), 2) ?? 0;
    if (data.taxable && data.taxable <= calcTaxable) {
      data.taxable = null;
    }
    const calcTaxfree = Numeric.round(Math.max(0, total - deduction - (data.taxable ?? calcTaxable)), 2) ?? 0;

    this._calculatedTaxable = data.taxable ?? calcTaxable;
    this._calculatedTaxfree = calcTaxfree;
  }

  /**
   * Gets validation message for the usecase.
   * @param row Row to validate
   * @returns A message that is either an error or OK (default). Warnings, info etc. may later be supported.
   */
  public getUcValidation(row: UserDefinedRow): UsecaseValidationMessage {
    const data: SubsidisedCommuteUsecase = row?.data || {};
    let msg;
    switch (data.kind) {
      case SubsidisedCommuteKind.SingleDeduction:
        msg = `${Translations.get("SALAXY.VALIDATION.Usecases.subsidisedCommute.singleDeduction")} ${Numeric.formatPrice(data.deduction || 0)}`;
        break;
      case SubsidisedCommuteKind.PeriodicalDeduction:
        msg = `${Translations.get("SALAXY.VALIDATION.Usecases.subsidisedCommute.periodicalDeduction")} ${Numeric.formatPrice(data.deduction || 0)} ${Translations.get("SALAXY.VALIDATION.Usecases.subsidisedCommute.periodicalDeduction2")} ${data.periodDivider || 12} ${Translations.get("SALAXY.VALIDATION.Usecases.subsidisedCommute.periodicalDeduction3")}`;
        break;
      case SubsidisedCommuteKind.NoDeduction:
      default:
        msg = "SALAXY.VALIDATION.Usecases.subsidisedCommute.noDeduction";
        break;
    }
    return { type: "default", msg };
  }
}
